import { Plus, X, Boxes } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const PotentialMachinesEditor = ({ value = [], onChange, catalog = [] }) => {
  const update = (i, patch) => onChange(value.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const add = () => onChange([...value, { catalog_id: "", quantity: 1 }]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div data-testid="potential-machines-editor">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Potential machines</Label>
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="pm-add-row-btn">
          <Plus className="w-3 h-3 mr-1" /> Add machine
        </Button>
      </div>
      {value.length === 0 ? (
        <div className="mt-1 text-xs text-slate-400 border border-dashed border-slate-200 rounded-md px-3 py-3" data-testid="pm-empty">
          No machines added yet. Add the models and quantities this prospect is interested in.
        </div>
      ) : (
        <div className="mt-1 space-y-2">
          {value.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`pm-row-${i}`}>
              <Select value={r.catalog_id || ""} onValueChange={(v) => update(i, { catalog_id: v })}>
                <SelectTrigger className="flex-1 min-w-0" data-testid={`pm-model-select-${i}`}><SelectValue placeholder="Pick a model" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {catalog.map((m) => (
                    <SelectItem key={m.id} value={m.id} disabled={m.id !== r.catalog_id && value.some((x) => x.catalog_id === m.id)}>
                      {m.model_name}{m.manufacturer ? ` — ${m.manufacturer}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="number" min="1" step="1" inputMode="numeric" className="w-20" value={r.quantity}
                onChange={(e) => update(i, { quantity: e.target.value })} aria-label="Quantity" data-testid={`pm-qty-input-${i}`} />
              <Button type="button" size="sm" variant="ghost" onClick={() => remove(i)} className="text-rose-500 hover:bg-rose-50 px-2" data-testid={`pm-remove-btn-${i}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const PotentialMachinesSummary = ({ machines = [], testId }) => {
  if (!machines.length) return null;
  const units = machines.reduce((s, m) => s + (Number(m.quantity) || 0), 0);
  const label = `${machines.length} model${machines.length > 1 ? "s" : ""} · ${units} unit${units !== 1 ? "s" : ""}`;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5 cursor-help"
            data-testid={testId}>
            <Boxes className="w-3 h-3" /> {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs" data-testid={testId ? `${testId}-tooltip` : undefined}>
          <ul className="space-y-0.5 text-xs">
            {machines.map((m) => (
              <li key={m.catalog_id} className="flex justify-between gap-4">
                <span className="truncate">{m.model_name || "Unknown model"}</span>
                <span className="font-semibold">× {m.quantity}</span>
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const cleanPotentialMachines = (rows = []) =>
  rows.filter((r) => r.catalog_id).map((r) => ({ catalog_id: r.catalog_id, quantity: Math.max(1, parseInt(r.quantity, 10) || 1) }));
